/*
 * Lenis init — Pause Rituals
 * -----------------------------------------------------------------------------
 * Loaded only when Theme settings → Scrolling → Engine is "Lenis".
 * assets/lenis.min.js must be loaded before this file.
 *
 * Why Lenis is usable here at all
 * ------------------------------
 * Lenis v1 does NOT transform a wrapper element. It intercepts wheel input and
 * then moves the real document scroll position. That matters more than anything
 * else in this theme, because it means all of the following keep working with no
 * changes at all:
 *
 *   • position: sticky        the header, the PDP image column, facets
 *   • IntersectionObserver    every reveal in pause-motion.js
 *   • window.scrollY reads    the header's own show/hide threshold
 *   • browser scroll restore  back/forward returns to the right place
 *
 * The wrapper-transform approach used by older smooth-scroll libraries breaks
 * all four. That is the difference, and it is why this is worth doing.
 *
 * What DOES need wiring, and is wired below
 * -----------------------------------------
 * Lenis keeps its own rAF loop running regardless of `overflow: hidden` on the
 * body, so an open cart or menu drawer would still scroll the page behind it.
 * The drawers in this theme all signal themselves the same way — they add
 * `overflow-hidden` (or `overflow-hidden-<breakpoint>`) to <body> — so a single
 * observer on that class can stop and start Lenis for all of them without
 * touching cart-drawer.js, global.js, or any other file.
 *
 * Deliberately NOT smoothing touch
 * --------------------------------
 * `syncTouch` is left off (Lenis's own default). Phones already have OS momentum
 * scrolling, pull-to-refresh and the iOS address-bar collapse, and easing on top
 * of that fights all three. This is also what keeps scroll-lock.js relevant and
 * correct on mobile — it blocks touchmove behind an open drawer, which is a
 * different mechanism from the one above and is still needed.
 */
(function () {
  'use strict';

  if (typeof window.Lenis !== 'function') return;

  /* ---- Opt out completely, rather than degrade --------------------------- */

  // Someone who asked for less motion gets the browser's plain scroll.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // The theme editor scrolls the page itself when a section is selected, and
  // fighting it makes sections jump when a merchant clicks one.
  if (window.Shopify && window.Shopify.designMode) return;

  var config = window.themeSmoothScroll || {};

  /*
   * Lenis takes `lerp` as the fraction of remaining distance covered per frame —
   * the same shape as the previous script's "ease", so the three Feel settings
   * map across directly and the setting keeps meaning what it said.
   *   subtle 0.18 → short glide      smooth 0.12 → balanced      slow 0.075
   */
  var lerp = parseFloat(config.ease);
  if (!(lerp > 0 && lerp < 1)) lerp = 0.12;

  var lenis = new window.Lenis({
    lerp: lerp,
    wheelMultiplier: parseFloat(config.distance) || 1,

    // Touch stays native. See the note above.
    syncTouch: false,
    smoothWheel: true,

    // Let a real scrollable panel inside the page (the cart item list, a facet
    // drawer, an overflowing table) take the gesture instead of the document.
    prevent: function (node) {
      return node.hasAttribute && node.hasAttribute('data-lenis-prevent');
    }
  });

  window.themeLenis = lenis;

  var rafId = null;
  function raf(time) {
    lenis.raf(time);
    rafId = window.requestAnimationFrame(raf);
  }
  rafId = window.requestAnimationFrame(raf);

  /* ---- Drawers and modals ------------------------------------------------
   * Every overlay in the theme locks the page by adding an `overflow-hidden`
   * class to <body> (cart-drawer.js:49, global.js:637, and the breakpoint
   * variants used by the menu drawer). Watching that one attribute covers all
   * of them, including any added later, without editing their files.
   */
  var body = document.body;

  function locked() {
    for (var i = 0; i < body.classList.length; i++) {
      if (body.classList[i].indexOf('overflow-hidden') === 0) return true;
    }
    return false;
  }

  function syncLock() {
    if (locked()) lenis.stop();
    else lenis.start();
  }

  new MutationObserver(syncLock).observe(body, {
    attributes: true,
    attributeFilter: ['class']
  });
  syncLock();

  /* ---- In-page anchors ---------------------------------------------------
   * Native anchor jumps still work, but they land instantly while every other
   * scroll on the page is eased, which reads as a glitch. Routing them through
   * Lenis keeps one motion language.
   *
   * Skipped deliberately:
   *   href="#"            not a destination
   *   [data-lenis-ignore] escape hatch
   *   modified clicks     ctrl/cmd/shift-click opens a tab; never intercept
   *   missing targets     let the browser do whatever it would have done
   */
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var link = e.target.closest ? e.target.closest('a[href^="#"]') : null;
    if (!link || link.hasAttribute('data-lenis-ignore')) return;

    var hash = link.getAttribute('href');
    if (!hash || hash === '#') return;

    var target;
    try {
      target = document.querySelector(hash);
    } catch (err) {
      return; // not a valid selector — leave it alone
    }
    if (!target) return;

    e.preventDefault();
    lenis.scrollTo(target, { offset: 0 });
  });

  /* Section re-render in the Theme Editor can change page height. */
  document.addEventListener('shopify:section:load', function () {
    lenis.resize();
  });
}());
